// frontend/UserSubscriptionList.jsx
import { useContext, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import apiService from '../../../services/apiService';
import { toast } from 'react-toastify';
import Table from '../../../components/Table';
import { FaFileCsv, FaTrash, FaArrowLeft, FaPrint } from 'react-icons/fa';

const UserSubscriptionList = () => {
  const { user } = useContext(AuthContext);
  const { userId } = useParams();

  const [data, setData] = useState({
    current: { active: false },
    history: [],
    userName: '',
    email: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admins only');
      return;
    }
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await apiService.get(`/subscriptions/${userId}`);
      setData(res.data || res);
    } catch (err) {
      setError('Failed to load');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel active subscription?')) return;
    try {
      await apiService.delete(`/subscriptions/${userId}`);
      toast.success('Cancelled');
      fetchData();
    } catch {
      toast.error('Failed');
    }
  };

  const handleExportCsv = async () => {
    try {
      const res = await apiService.get(`/subscriptions/export/csv?userId=${userId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `history-${userId}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A';

  const badge = (s) => {
    const map = { active: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800', expired: 'bg-gray-100 text-gray-800', upgraded: 'bg-blue-100 text-blue-800' };
    return <span className={`px-3 py-1 rounded-full text-xs font-medium ${map[s] || 'bg-gray-100'}`}>{s.toUpperCase()}</span>;
  };

  // PRINT ANY INVOICE (current or history)
  const printInvoice = (sub) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const invoiceNum = `CFE-${Date.now().toString().slice(-6)}`;

    const invoiceHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Invoice - ${data.userName}</title>
        <style>
          body { font-family: 'Segoe UI', sans-serif; margin: 40px; color: #1f2937; line-height: 1.6; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; }
          .logo img { height: 80px; }
          .company { text-align: right; }
          .company h1 { margin: 0 0 8px; color: #1e40af; font-size: 28px; }
          .invoice-meta { display: flex; justify-content: space-between; padding: 20px; background: #f8fafc; border-radius: 12px; margin: 30px 0; }
          .meta-box { flex: 1; }
          table { width: 100%; border-collapse: collapse; margin: 30px 0; }
          th, td { padding: 14px; text-align: left; border-bottom: 1px solid #e2e8f0; }
          th { background: #eef2ff; color: #4f46e5; font-weight: 600; }
          .total { font-size: 22px; font-weight: bold; text-align: right; margin-top: 30px; }
          .footer { margin-top: 80px; text-align: center; color: #64748b; font-size: 12px; }
          @media print {
            body { margin: 15px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">
            <img src="/logos/logo.png" alt="Chicken Farm Experts" />
          </div>
          <div class="company">
            <h1>INVOICE</h1>
            <p><strong>#${invoiceNum}</strong></p>
            <p><strong>Date:</strong> ${new Date().toLocaleDateString('en-GB')}</p>
            <p><strong>Status:</strong> <span style="color:#16a34a">${sub.status?.toUpperCase()}</span></p>
          </div>
        </div>

        <div class="invoice-meta">
          <div class="meta-box">
            <strong>Bill To:</strong><br>
            ${data.userName}<br>
            ${data.email}<br>
            Customer ID: ${userId}
          </div>
          <div class="meta-box" style="text-align:right">
            <strong>Chicken Farm Experts</strong><br>
            51 Queen Ediths Way<br>
            Cambridge, CB1 8PJ<br>
            United Kingdom<br><br>
            +44 161 566 9077<br>
            info@chickenfarmexperts.com<br>
            VAT: GB123456789
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Period</th>
              <th class="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>${sub.plan || sub.name || 'Subscription Plan'}</strong></td>
              <td>${formatDate(sub.startDate)} – ${formatDate(sub.endDate)}</td>
              <td class="text-right">£${sub.price || '0.00'}</td>
            </tr>
          </tbody>
        </table>

        <div class="total">
          Total: <span style="color:#1e40af;">£${sub.price || '0.00'}</span>
        </div>

        <div class="footer">
          <p>Thank you for your business!</p>
          <p>Payment terms: Due on receipt • Bank: HSBC • IBAN: GB29HBUK12345678901234</p>
        </div>

        <script>setTimeout(() => window.print(), 600);</script>
      </body>
      </html>
    `;

    printWindow.document.write(invoiceHtml);
    printWindow.document.close();
  };

  const historyColumns = [
    { key: 'name', label: 'Plan' },
    { key: 'price', label: 'Price', render: r => `£${r.price}` },
    { key: 'status', label: 'Status', render: r => badge(r.status) },
    { key: 'startDate', label: 'Start', render: r => formatDate(r.startDate) },
    { key: 'endDate', label: 'End', render: r => formatDate(r.endDate) },
    {
      key: 'actions',
      label: 'Print',
      render: (r) => (
        <button
          onClick={() => printInvoice(r)}
          className="text-purple-600 hover:text-purple-800 font-medium text-sm"
        >
          Print Invoice
        </button>
      )
    }
  ];

  if (isLoading) return <div className="flex justify-center py-20"><div className="animate-spin h-12 w-12 border-4 border-indigo-600 rounded-full border-t-transparent"></div></div>;
  if (error) return <div className="text-center py-20 text-red-600">{error} <button onClick={fetchData} className="ml-2 text-indigo-600 underline">Retry</button></div>;

  const hasActive = data.current.active;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        <Link to="/admin/subscriptions" className="inline-flex items-center text-indigo-600 hover:text-indigo-800 mb-6">
          <FaArrowLeft className="mr-2" /> Back
        </Link>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-extrabold text-gray-900">Subscription History</h1>
          <p className="mt-2 text-xl text-gray-600">{data.userName} • {data.email}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
          <div className="flex flex-wrap gap-3 justify-end mb-6">
            <button onClick={handleExportCsv} className="flex items-center bg-green-600 text-white px-5 py-2.5 rounded-lg hover:bg-green-700">
              <FaFileCsv className="mr-2" /> Export CSV
            </button>
            {hasActive && (
              <button onClick={() => printInvoice(data.current)} className="flex items-center bg-purple-600 text-white px-5 py-2.5 rounded-lg hover:bg-purple-700">
                <FaPrint className="mr-2" /> Print Current Invoice
              </button>
            )}
          </div>

          {/* Current */}
          <div className="p-8 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl mb-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Current Subscription</h2>
              {hasActive && badge('active')}
            </div>
            {hasActive ? (
              <div className="grid md:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-xl shadow">
                  <p className="text-sm text-gray-600">Plan</p>
                  <p className="text-2xl font-bold text-indigo-600">{data.current.plan?.toUpperCase()}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow">
                  <p className="text-sm text-gray-600">Price</p>
                  <p className="text-2xl font-bold">£{data.current.price}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow">
                  <p className="text-sm text-gray-600">Period</p>
                  <p>{formatDate(data.current.startDate)} – {formatDate(data.current.endDate)}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow">
                  <button onClick={handleCancel} className="bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 flex items-center justify-center">
                    <FaTrash className="mr-2" /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-center py-10 text-gray-500 text-lg">No active subscription</p>
            )}
          </div>

          {/* History Table */}
          <h2 className="text-2xl font-bold mb-6">Past Subscriptions ({data.history.length})</h2>
          {data.history.length > 0 ? (
            <Table columns={historyColumns} data={data.history} />
          ) : (
            <p className="text-center py-12 text-gray-500">No past records</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserSubscriptionList;